import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import { Video } from "@remotion/media";
import { TerminalWindowDark } from "../shared/TerminalWindowDark";
import { CodeTypewriter } from "../shared/CodeTypewriter";
import { FadeInText } from "../shared/FadeInText";
import { DeviceFrame } from "../shared/DeviceFrame";
import { AnimatedCursor } from "../shared/AnimatedCursor";
import { SPRING_CONFIGS } from "../utils/springs";
import { COLORS } from "../utils/colors";
import { loadFont as loadNeonderthaw } from "@remotion/google-fonts/Neonderthaw";
import { loadFont as loadBowlbyOneSC } from "@remotion/google-fonts/BowlbyOneSC";
import { loadFont as loadBricolage } from "@remotion/google-fonts/BricolageGrotesque";
import { loadFont as loadMiltonianTattoo } from "@remotion/google-fonts/MiltonianTattoo";
import type { TemplateProps } from "../Root";

const { fontFamily: neonderthaw } = loadNeonderthaw();
const { fontFamily: bowlbyOneSC } = loadBowlbyOneSC();
const { fontFamily: bricolage } = loadBricolage();
const { fontFamily: miltonianTattoo } = loadMiltonianTattoo();

// Font families for staggered title lines (alternating styles like V5)
const TITLE_FONTS = [neonderthaw, bowlbyOneSC, bricolage, miltonianTattoo];
const TITLE_SIZES = [120, 96, 88, 80];

/**
 * Showcase Template (based on tuka's CreativeV5ArtisticFloat)
 *
 * Two modes:
 *   'artistic': Staggered fonts, no terminal — default when no tabs
 *   'terminal': Plain title (FadeInText) + tabbed terminal — default when tabs exist
 *
 * Layout: Title (top-left) + Terminal if terminal mode (bottom-left) + Floating device (right)
 * Timeline (12s = 360 frames):
 *   0-30:   Device enters center with bouncy spring
 *   30-60:  Device zoom in (1x → 1.3x)
 *   60-90:  Cursor appears, moves to device, clicks
 *   90-120: Device bounces back (spring)
 *   120-160: Device slides to right side
 *   140+:   Text appears (artistic staggered or plain FadeInText)
 *   160+:   Terminal appears below text (if terminal mode), gentle floating on device
 */
export const Showcase: React.FC<TemplateProps> = ({
  videoUrl,
  platform,
  title,
  titleLines,
  subtitle,
  terminalTabs,
  showcaseMode,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const hasTabs = terminalTabs && terminalTabs.length > 0;

  // Determine effective mode
  const effectiveMode = showcaseMode || (hasTabs ? "terminal" : "artistic");

  // Device dimensions
  const isWeb = platform === "web";
  const phoneHeight = isWeb ? 640 : 820;
  const phoneWidth = isWeb ? 900 : phoneHeight * (1043 / 2063);

  // --- Device entrance ---
  const deviceEntrance = spring({
    frame,
    fps,
    delay: 0,
    config: SPRING_CONFIGS.bouncy,
  });
  const deviceEntranceScale = interpolate(deviceEntrance, [0, 1], [0.8, 1]);
  const deviceEntranceOpacity = interpolate(deviceEntrance, [0, 1], [0, 1]);

  // --- Zoom in (30-60) ---
  const zoomProgress = interpolate(frame, [30, 60], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.inOut(Easing.cubic),
  });
  const zoomScale = interpolate(zoomProgress, [0, 1], [1, 1.3]);

  // --- Bounce back (90-120) ---
  const bounceProgress = spring({
    frame,
    fps,
    delay: 90,
    config: SPRING_CONFIGS.bouncy,
  });
  const bounceScale = frame >= 90
    ? interpolate(bounceProgress, [0, 1], [1.3, 1])
    : 1;

  // --- Slide to right (120-160) ---
  const slideProgress = interpolate(frame, [120, 160], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.inOut(Easing.cubic),
  });

  const deviceCenterX = 960 - phoneWidth / 2;
  const deviceRightX = 1920 - phoneWidth - 120;
  const deviceX = interpolate(slideProgress, [0, 1], [deviceCenterX, deviceRightX]);

  // Device final scale
  let deviceFinalScale = deviceEntranceScale;
  if (frame >= 30 && frame < 90) {
    deviceFinalScale = zoomScale;
  } else if (frame >= 90) {
    deviceFinalScale = bounceScale;
  }

  // Gentle floating after settled
  const floatY = frame > 160 ? Math.sin((frame - 160) * 0.04) * 4 : 0;

  // --- Text appear frame ---
  const textAppearFrame = 140;
  const showText = frame >= textAppearFrame;

  // --- Terminal entrance ---
  const terminalAppearFrame = 170;
  const terminalProgress = spring({
    frame,
    fps,
    delay: terminalAppearFrame,
    config: SPRING_CONFIGS.smooth,
  });
  const terminalOpacity = interpolate(terminalProgress, [0, 1], [0, 1]);
  const terminalSlideY = interpolate(terminalProgress, [0, 1], [30, 0]);

  // Cursor visible during zoom phase
  const showCursor = frame >= 55 && frame < 95;

  // Dynamic tab duration based on content length
  const calculateTabDuration = () => {
    if (!hasTabs) return 100;
    const minDuration = 90; // 3s minimum
    const charFrames = 2;
    const maxContentLength = Math.max(...terminalTabs.map((t) => t.content.length));
    const framesNeeded = maxContentLength * charFrames + 30; // +30 for fade transitions
    // Cap so all tabs fit in composition (360 frames - terminalAppearFrame - 20 delay)
    const availableFrames = 360 - terminalAppearFrame - 20;
    const maxPerTab = Math.floor(availableFrames / terminalTabs.length);
    return Math.min(Math.max(minDuration, framesNeeded), maxPerTab);
  };
  const tabDurationFrames = calculateTabDuration();

  // Dynamic terminal height based on content
  const calculateTerminalHeight = () => {
    if (!hasTabs) return 280;
    const codeFontSize = 13;
    const lineHeight = codeFontSize * 1.7; // matches CodeTypewriter lineHeight
    const maxLines = Math.max(...terminalTabs.map((t) => t.content.split("\n").length));
    const contentPadding = 24 * 2; // top + bottom padding in TerminalWindowDark
    const titleBarHeight = 40;
    const tabBarHeight = terminalTabs.length > 1 ? 30 : 0;
    const computed = titleBarHeight + tabBarHeight + contentPadding + maxLines * lineHeight;
    // Clamp: min 200, max ~600 to leave room for title + padding
    return Math.round(Math.min(Math.max(computed, 200), 600));
  };
  const terminalHeight = calculateTerminalHeight();

  // Platform label
  const platformLabel =
    platform === "ios"
      ? "iOS App"
      : platform === "android"
        ? "Android App"
        : "Web App";

  // --- Title text config (shared between modes) ---
  const titleProgress = spring({
    frame,
    fps,
    delay: textAppearFrame,
    config: SPRING_CONFIGS.smooth,
  });
  const titleOpacity = interpolate(titleProgress, [0, 1], [0, 1]);

  // Build display lines for artistic mode
  const lineDelay = 8;
  const displayLines: string[] = titleLines && titleLines.length > 0
    ? titleLines
    : (() => {
        const words = title.split(" ").slice(0, 8);
        const lines: string[] = [];
        for (let i = 0; i < words.length; i += 2) {
          lines.push(words.slice(i, i + 2).join(" "));
        }
        return lines;
      })();

  const makeLineProgress = (index: number) =>
    spring({
      frame,
      fps,
      delay: textAppearFrame + index * lineDelay,
      config: { damping: 18, stiffness: 160, mass: 0.6 },
    });

  return (
    <AbsoluteFill style={{ backgroundColor: "#FFFFFF" }}>
      {/* Left side content */}
      {showText && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: deviceRightX - 40,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: 6,
            paddingLeft: 100,
            paddingTop: 100,
          }}
        >
          {effectiveMode === "artistic" ? (
            /* Artistic mode: staggered lines with different fonts */
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                maxWidth: 600,
              }}
            >
              {displayLines.map((line, idx) => {
                const progress = makeLineProgress(idx);
                const opacity = interpolate(progress, [0, 1], [0, 1]);
                const translateY = interpolate(progress, [0, 1], [40, 0]);

                const fontIdx = idx % TITLE_FONTS.length;
                const isStroke = idx === 0;

                return (
                  <span
                    key={idx}
                    style={{
                      fontFamily: TITLE_FONTS[fontIdx],
                      fontSize: TITLE_SIZES[fontIdx],
                      fontWeight:
                        fontIdx === 0 || fontIdx === 3
                          ? 400
                          : fontIdx === 2
                            ? 600
                            : 400,
                      color: isStroke ? "transparent" : "#000000",
                      WebkitTextStroke: isStroke ? "2.5px #000000" : undefined,
                      opacity,
                      transform: `translateY(${translateY}px)`,
                      display: "block",
                      lineHeight: 1.1,
                    }}
                  >
                    {line}
                  </span>
                );
              })}
            </div>
          ) : (
            /* Terminal mode: plain title like Studio */
            <div style={{ opacity: titleOpacity, maxWidth: 580 }}>
              <FadeInText
                delay={textAppearFrame}
                fontSize={14}
                fontWeight={700}
                color={COLORS.accent}
                style={{ textTransform: "uppercase", letterSpacing: 3 }}
              >
                {platformLabel}
              </FadeInText>
              <FadeInText
                delay={textAppearFrame + 5}
                fontSize={38}
                fontWeight={800}
                color="#000000"
                style={{ marginTop: 8, lineHeight: 1.2 }}
              >
                {title}
              </FadeInText>
              {subtitle && (
                <FadeInText
                  delay={textAppearFrame + 10}
                  fontSize={16}
                  fontWeight={400}
                  color="#6B7280"
                  style={{ marginTop: 6 }}
                >
                  {subtitle}
                </FadeInText>
              )}
            </div>
          )}

          {/* Terminal below title (only in terminal mode with tabs) */}
          {effectiveMode === "terminal" && hasTabs && (
            <div
              style={{
                marginTop: 30,
                width: "100%",
                maxWidth: 780,
                height: terminalHeight,
                opacity: terminalOpacity,
                transform: `translateY(${terminalSlideY}px)`,
              }}
            >
              <TerminalWindowDark
                title="network-trace.json"
                tabs={terminalTabs}
                tabDurationFrames={tabDurationFrames}
                renderTab={(activeIndex) => (
                  <CodeTypewriter
                    code={terminalTabs[activeIndex]?.content || ""}
                    charFrames={2}
                    delay={terminalAppearFrame + 20}
                    fontSize={13}
                  />
                )}
              />
            </div>
          )}
        </div>
      )}

      {/* Device */}
      <div
        style={{
          position: "absolute",
          left: deviceX,
          top: 540 - phoneHeight / 2 + floatY,
          width: phoneWidth,
          height: phoneHeight,
          opacity: deviceEntranceOpacity,
          transform: `scale(${deviceFinalScale})`,
          transformOrigin: "center center",
        }}
      >
        <DeviceFrame platform={platform} width={phoneWidth} height={phoneHeight}>
          <Video
            src={videoUrl}
            muted
            loop
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </DeviceFrame>
      </div>

      {/* Animated cursor */}
      {showCursor && (
        <AnimatedCursor
          size={48}
          path={[
            { x: 1400, y: 200, atFrame: 55 },
            { x: 960, y: 540, atFrame: 75, click: true },
            { x: 980, y: 560, atFrame: 90 },
          ]}
        />
      )}
    </AbsoluteFill>
  );
};
